import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useApp } from "../../context/AppContext";
import * as api from "../../lib/api";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import { Input } from "../ui/input";
import { UserSearchSelect } from "../ui/UserSearchSelect";
import { LeftCompanyBanner } from "../shared/LeftCompany";
import { hasLeftCompany } from "../../lib/utils";
import {
    Monitor, Key, AppWindow, ShieldCheck, Package, ArrowLeftRight,
    AlertTriangle, Undo2, Trash2, Plus, Clock, Ticket, Phone, Mail,
    Building2, Briefcase, ChevronRight, Laptop, Smartphone, Tablet,
    Printer, HardDrive, Eye, EyeOff, FileSpreadsheet
} from "lucide-react";

const TYPE_CONFIG = {
    equipment: { icon: Monitor, label: "Equipement", color: "bg-blue-100 text-blue-700" },
    license: { icon: Key, label: "Licence", color: "bg-purple-100 text-purple-700" },
    software: { icon: AppWindow, label: "Logiciel", color: "bg-cyan-100 text-cyan-700" },
    access: { icon: ShieldCheck, label: "Accès", color: "bg-green-100 text-green-700" },
    other: { icon: Package, label: "Autre", color: "bg-gray-100 text-gray-600" },
};

const STATUS_CONFIG = {
    active: { label: "Actif", color: "bg-emerald-100 text-emerald-700" },
    returned: { label: "Restitué", color: "bg-gray-200 text-gray-600" },
    transferred: { label: "Transféré", color: "bg-amber-100 text-amber-700" },
    lost: { label: "Perdu", color: "bg-red-100 text-red-700" },
    broken: { label: "Endommagé", color: "bg-orange-100 text-orange-700" },
};

const DEVICE_TYPE_CONFIG = {
    pc: { icon: Monitor, label: "PC", color: "bg-blue-100 text-blue-700" },
    laptop: { icon: Laptop, label: "Laptop", color: "bg-indigo-100 text-indigo-700" },
    smartphone: { icon: Smartphone, label: "Smartphone", color: "bg-green-100 text-green-700" },
    tablet: { icon: Tablet, label: "Tablette", color: "bg-violet-100 text-violet-700" },
    printer: { icon: Printer, label: "Imprimante", color: "bg-gray-100 text-gray-600" },
    other: { icon: HardDrive, label: "Autre", color: "bg-gray-100 text-gray-600" },
};

const LICENSE_STATUS = {
    active: { label: "Active", color: "bg-emerald-100 text-emerald-700" },
    expired: { label: "Expirée", color: "bg-red-100 text-red-700" },
    revoked: { label: "Révoquée", color: "bg-gray-200 text-gray-600" },
};

const ROLE_LABELS = { admin: "Administrateur", agent: "Agent IT", coordinator: "Coordinateur", approver: "Approbateur", user: "Utilisateur" };

const TABS = [
    { id: "overview", label: "Vue d'ensemble" },
    { id: "devices", label: "Appareils" },
    { id: "licenses", label: "Licences" },
    { id: "inventory", label: "Inventaire" },
    { id: "assets", label: "Equipement & Assets" },
    { id: "tickets", label: "Tickets" },
];

export default function UserProfile() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { currentUser, getUserById, users, departments, hasPermission } = useApp();
    const [user, setUser] = useState(null);
    const [assets, setAssets] = useState([]);
    const [allAssets, setAllAssets] = useState([]);
    const [summary, setSummary] = useState(null);
    const [tickets, setTickets] = useState([]);
    const [devices, setDevices] = useState([]);
    const [licenses, setLicenses] = useState([]);
    const [customFields, setCustomFields] = useState([]);
    const [inventoryItems, setInventoryItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState("overview");
    const [showReturned, setShowReturned] = useState(false);
    const [returnDialog, setReturnDialog] = useState(null);
    const [transferDialog, setTransferDialog] = useState(null);
    const [addAssetDialog, setAddAssetDialog] = useState(false);
    const [addDeviceDialog, setAddDeviceDialog] = useState(false);
    const [addLicenseDialog, setAddLicenseDialog] = useState(false);
    const [returnCondition, setReturnCondition] = useState("");
    const [returnNotes, setReturnNotes] = useState("");
    const [transferTarget, setTransferTarget] = useState(null);
    const [addAssetForm, setAddAssetForm] = useState({ type: "equipment", name: "", description: "", serial_number: "", notes: "" });
    const [addDeviceForm, setAddDeviceForm] = useState({ device_type: "laptop", display_name: "", manufacturer: "", model: "", serial_number: "", operating_system: "", os_version: "", pc_name: "", trust_type: "", is_managed: false, notes: "" });
    const [addLicenseForm, setAddLicenseForm] = useState({ name: "", license_type: "subscription", vendor: "", license_key: "", start_date: "", end_date: "", cost: "", currency: "CHF", notes: "" });
    const [exporting, setExporting] = useState(false);

    const isAgent = hasPermission("agent");

    const loadData = useCallback(async () => {
        try {
            const [activeAssets, allA, sum, devs, lics, cfs, invItems] = await Promise.all([
                api.getUserAssets(id, { status: "active" }),
                api.getUserAssets(id, { status: "all" }),
                api.getUserAssetsSummary(id),
                api.getUserDevices(id, { status: "all" }),
                api.getUserLicenses(id, { status: "all" }),
                api.getCustomFields(),
                api.getInventoryByUser(id).catch(() => []),
            ]);
            setAssets(activeAssets);
            setAllAssets(allA);
            setSummary(sum);
            setDevices(devs);
            setLicenses(lics);
            setCustomFields(cfs);
            setInventoryItems(invItems);

            try {
                const data = await api.searchTicketsGlobal({ user_id: id, limit: 50 });
                setTickets(data.tickets || []);
            } catch { setTickets([]); }
        } catch (e) {
            if (e.response?.status === 403) {
                toast.error("Accès refusé à ce profil");
                navigate("/");
            }
        } finally {
            setLoading(false);
        }
    }, [id, navigate]);

    useEffect(() => {
        const u = getUserById(id);
        setUser(u);
        loadData();
    }, [id, getUserById, loadData]);

    // ========== ACTIONS ==========
    const handleReturn = async () => {
        if (!returnDialog) return;
        try {
            await api.returnAsset(returnDialog.id, { return_condition: returnCondition, notes: returnNotes });
            toast.success(`${returnDialog.name} restitué`);
            setReturnDialog(null);
            setReturnCondition("");
            setReturnNotes("");
            loadData();
        } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    };

    const handleTransfer = async () => {
        if (!transferDialog || !transferTarget) return;
        try {
            await api.transferAsset(transferDialog.id, { target_user_id: transferTarget });
            toast.success(`${transferDialog.name} transféré`);
            setTransferDialog(null);
            setTransferTarget(null);
            loadData();
        } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    };

    const handleMarkLost = async (asset) => {
        if (!window.confirm(`Marquer "${asset.name}" comme perdu ?`)) return;
        try { await api.markAssetLost(asset.id, {}); toast.success("Marqué comme perdu"); loadData(); } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    };
    const handleMarkBroken = async (asset) => {
        if (!window.confirm(`Marquer "${asset.name}" comme endommagé ?`)) return;
        try { await api.markAssetBroken(asset.id, {}); toast.success("Marqué comme endommagé"); loadData(); } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    };

    const handleAddAsset = async () => {
        if (!addAssetForm.name.trim()) { toast.error("Nom requis"); return; }
        try {
            await api.createUserAsset(id, addAssetForm);
            toast.success(`Asset "${addAssetForm.name}" créé`);
            setAddAssetDialog(false);
            setAddAssetForm({ type: "equipment", name: "", description: "", serial_number: "", notes: "" });
            loadData();
        } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    };

    const handleAddDevice = async () => {
        if (!addDeviceForm.display_name.trim()) { toast.error("Nom requis"); return; }
        try {
            await api.createUserDevice(id, addDeviceForm);
            toast.success(`Appareil "${addDeviceForm.display_name}" ajouté`);
            setAddDeviceDialog(false);
            setAddDeviceForm({ device_type: "laptop", display_name: "", manufacturer: "", model: "", serial_number: "", operating_system: "", os_version: "", pc_name: "", trust_type: "", is_managed: false, notes: "" });
            loadData();
        } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    };

    const handleAddLicense = async () => {
        if (!addLicenseForm.name.trim()) { toast.error("Nom requis"); return; }
        const payload = { ...addLicenseForm };
        if (payload.cost) payload.cost = parseFloat(payload.cost);
        else delete payload.cost;
        if (!payload.end_date) payload.end_date = null;
        try {
            await api.createUserLicense(id, payload);
            toast.success(`Licence "${addLicenseForm.name}" ajoutée`);
            setAddLicenseDialog(false);
            setAddLicenseForm({ name: "", license_type: "subscription", vendor: "", license_key: "", start_date: "", end_date: "", cost: "", currency: "CHF", notes: "" });
            loadData();
        } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    };

    const handleReturnDevice = async (device) => {
        if (!window.confirm(`Restituer "${device.display_name}" ?`)) return;
        try { await api.returnDevice(device.id, {}); toast.success("Appareil restitué"); loadData(); } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    };

    const handleRevokeLicense = async (lic) => {
        if (!window.confirm(`Révoquer la licence "${lic.name}" ?`)) return;
        try { await api.revokeLicense(lic.id, {}); toast.success("Licence révoquée"); loadData(); } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    };

    const handleExport = async () => {
        setExporting(true);
        try {
            const response = await api.exportUsersExcel({ user_id: id });
            const url = URL.createObjectURL(response.data);
            const a = document.createElement("a");
            a.href = url;
            a.download = `export_${user?.first_name}_${user?.last_name}_${new Date().toISOString().slice(0, 10)}.xlsx`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
        } catch { toast.error("Erreur lors de l'export"); }
        finally { setExporting(false); }
    };

    const dept = user?.department_id ? departments.find((d) => d.id === user.department_id) : null;

    if (loading) return <div className="page-container"><div className="animate-pulse text-gray-400">Chargement du profil...</div></div>;
    if (!user) return <div className="page-container"><p className="text-gray-500">Utilisateur introuvable</p></div>;

    const activeDevices = devices.filter((d) => d.status === "active");
    const displayDevices = showReturned ? devices : activeDevices;
    const activeLicenses = licenses.filter((l) => l.status === "active");
    const equipment = assets.filter((a) => a.type === "equipment");
    const softLicenses = assets.filter((a) => a.type === "license" || a.type === "software");
    const accesses = assets.filter((a) => a.type === "access");

    return (
        <div className="page-container" data-testid="user-profile-page">
            <LeftCompanyBanner user={user} context="utilisateur" className="mb-4" />

            {/* HEADER */}
            <div className={`card mb-4 ${hasLeftCompany(user) ? "border-rose-200 bg-rose-50/30" : ""}`} data-testid="profile-header">
                <div className="card-body flex items-start gap-5">
                    <div className="w-14 h-14 rounded-full bg-gradient-to-br from-teal-500 to-cyan-600 flex items-center justify-center text-white font-bold text-lg flex-shrink-0">
                        {user.first_name?.[0]}{user.last_name?.[0]}
                    </div>
                    <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                            <h1 className="text-lg font-bold text-gray-900">{user.first_name} {user.last_name}</h1>
                            {user.employee_id && <span className="text-[10px] px-2 py-0.5 rounded bg-gray-100 text-gray-700 font-mono" title="N° employé (Microsoft 365)" data-testid="profile-employee-id">N° employé {user.employee_id}</span>}
                            <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${user.is_active !== false ? "bg-green-100 text-green-700" : "bg-red-100 text-red-600"}`}>
                                {user.is_active !== false ? "Actif" : "Inactif"}
                            </span>
                            <span className="text-[10px] px-2 py-0.5 rounded-full font-medium bg-gray-100 text-gray-600">{ROLE_LABELS[user.role] || user.role}</span>
                            {user.user_type === "external" && <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 font-bold">EXT</span>}
                        </div>
                        <div className="flex flex-wrap items-center gap-4 mt-2 text-xs text-gray-500">
                            {user.email && !user.email.includes("@no-email") && <span className="flex items-center gap-1"><Mail className="w-3 h-3" />{user.email}</span>}
                            {user.phone_fixed && <span className="flex items-center gap-1"><Phone className="w-3 h-3" />{user.phone_fixed}</span>}
                            {user.phone_mobile && <span className="flex items-center gap-1"><Phone className="w-3 h-3" />{user.phone_mobile}</span>}
                            {dept && <span className="flex items-center gap-1"><Building2 className="w-3 h-3" />{dept.name}</span>}
                        </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                        {isAgent && (
                            <Button variant="outline" size="sm" onClick={handleExport} disabled={exporting} data-testid="export-user-btn">
                                <FileSpreadsheet className="w-3.5 h-3.5 mr-1" />
                                {exporting ? "Export..." : "Exporter"}
                            </Button>
                        )}
                    </div>
                </div>
            </div>

            {/* TABS */}
            <div className="flex border-b border-gray-200 mb-4 overflow-x-auto" data-testid="profile-tabs">
                {TABS.map((tab) => (
                    <button
                        key={tab.id}
                        onClick={() => setActiveTab(tab.id)}
                        className={`px-4 py-2.5 text-xs font-medium whitespace-nowrap border-b-2 transition-colors ${activeTab === tab.id ? "border-teal-500 text-teal-700" : "border-transparent text-gray-500 hover:text-gray-700"}`}
                        data-testid={`tab-${tab.id}`}
                    >
                        {tab.label}
                    </button>
                ))}
            </div>

            {/* TAB CONTENT */}
            {activeTab === "overview" && <OverviewTab summary={summary} allAssets={allAssets} devices={activeDevices} licenses={activeLicenses} navigate={navigate} setActiveTab={setActiveTab} />}
            {activeTab === "devices" && <DevicesTab devices={displayDevices} showReturned={showReturned} setShowReturned={setShowReturned} isAgent={isAgent} onAdd={() => setAddDeviceDialog(true)} onReturn={handleReturnDevice} />}
            {activeTab === "licenses" && <LicensesTab licenses={licenses} isAgent={isAgent} onAdd={() => setAddLicenseDialog(true)} onRevoke={handleRevokeLicense} />}
            {activeTab === "inventory" && <InventoryTab items={inventoryItems} navigate={navigate} />}
            {activeTab === "assets" && <AssetsTab equipment={equipment} softLicenses={softLicenses} accesses={accesses} isAgent={isAgent} navigate={navigate} onAdd={() => setAddAssetDialog(true)} onReturn={setReturnDialog} onTransfer={setTransferDialog} onLost={handleMarkLost} onBroken={handleMarkBroken} />}
            {activeTab === "tickets" && <TicketsTab tickets={tickets} navigate={navigate} />}

            {/* DIALOGS */}
            <ReturnDialog returnDialog={returnDialog} setReturnDialog={setReturnDialog} returnCondition={returnCondition} setReturnCondition={setReturnCondition} returnNotes={returnNotes} setReturnNotes={setReturnNotes} onConfirm={handleReturn} />
            <TransferDialog transferDialog={transferDialog} setTransferDialog={setTransferDialog} transferTarget={transferTarget} setTransferTarget={setTransferTarget} users={users} departments={departments} userId={id} onConfirm={handleTransfer} />
            <AddAssetDialog open={addAssetDialog} onClose={() => setAddAssetDialog(false)} form={addAssetForm} setForm={setAddAssetForm} onConfirm={handleAddAsset} />
            <AddDeviceDialog open={addDeviceDialog} onClose={() => setAddDeviceDialog(false)} form={addDeviceForm} setForm={setAddDeviceForm} onConfirm={handleAddDevice} />
            <AddLicenseDialog open={addLicenseDialog} onClose={() => setAddLicenseDialog(false)} form={addLicenseForm} setForm={setAddLicenseForm} onConfirm={handleAddLicense} />
        </div>
    );
}

// ========== TAB COMPONENTS ==========

function OverviewTab({ summary, allAssets, devices, licenses, navigate, setActiveTab }) {
    return (
        <div className="space-y-4">
            {summary && (
                <div className="grid grid-cols-2 sm:grid-cols-5 gap-3" data-testid="overview-summary-cards">
                    <SummaryCard label="Equipements" count={summary.equipment_count} color="blue" onClick={() => setActiveTab("assets")} />
                    <SummaryCard label="Appareils" count={summary.devices_count || 0} color="indigo" onClick={() => setActiveTab("devices")} />
                    <SummaryCard label="Licences" count={summary.licenses_count || 0} color="purple" onClick={() => setActiveTab("licenses")} />
                    <SummaryCard label="Logiciels" count={summary.software_count} color="cyan" onClick={() => setActiveTab("assets")} />
                    <SummaryCard label="Accès" count={summary.access_count} color="green" onClick={() => setActiveTab("assets")} />
                </div>
            )}

            {/* Recent History */}
            <div className="card">
                <div className="card-header"><h3 className="text-sm font-semibold text-gray-800">Historique récent</h3></div>
                <div className="card-body p-0">
                    <div className="px-4 py-2 space-y-1 max-h-60 overflow-y-auto">
                        {allAssets.slice(0, 5).map((a) => {
                            const TypeIcon = TYPE_CONFIG[a.type]?.icon || Package;
                            const statusCfg = STATUS_CONFIG[a.status] || STATUS_CONFIG.active;
                            return (
                                <div key={a.id} className="flex items-center gap-3 py-1.5 border-b border-gray-50 last:border-0">
                                    <TypeIcon className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                                    <span className="text-xs text-gray-500 w-20 flex-shrink-0">{a.assigned_at ? new Date(a.assigned_at).toLocaleDateString("fr-CH") : ""}</span>
                                    <span className="text-xs font-medium text-gray-800 flex-1 truncate">{a.name}</span>
                                    <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-medium ${statusCfg.color}`}>{statusCfg.label}</span>
                                </div>
                            );
                        })}
                        {allAssets.length === 0 && <p className="text-xs text-gray-400 py-4 text-center">Aucun historique</p>}
                    </div>
                </div>
            </div>
        </div>
    );
}

function DevicesTab({ devices, showReturned, setShowReturned, isAgent, onAdd, onReturn }) {
    return (
        <div className="card">
            <div className="card-header flex items-center justify-between">
                <div className="flex items-center gap-2">
                    <Monitor className="w-4 h-4 text-gray-500" />
                    <h2 className="text-sm font-semibold text-gray-800">Appareils</h2>
                    <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-500 font-medium">{devices.length}</span>
                </div>
                <div className="flex items-center gap-2">
                    <label className="flex items-center gap-1.5 text-xs text-gray-500 cursor-pointer">
                        <input type="checkbox" checked={showReturned} onChange={(e) => setShowReturned(e.target.checked)} className="rounded border-gray-300" />
                        Afficher les restitués
                    </label>
                    {isAgent && <Button variant="outline" size="sm" onClick={onAdd} data-testid="add-device-btn"><Plus className="w-3 h-3 mr-1" />Ajouter</Button>}
                </div>
            </div>
            <div className="card-body p-0">
                {devices.length === 0 ? <p className="text-xs text-gray-400 py-6 text-center">Aucun appareil</p> : (
                    <table className="w-full text-sm">
                        <thead><tr className="border-b border-gray-100 text-left">
                            <th className="px-4 py-2 text-xs font-medium text-gray-500">Nom</th>
                            <th className="px-4 py-2 text-xs font-medium text-gray-500">Type</th>
                            <th className="px-4 py-2 text-xs font-medium text-gray-500">Modèle</th>
                            <th className="px-4 py-2 text-xs font-medium text-gray-500">N° série</th>
                            <th className="px-4 py-2 text-xs font-medium text-gray-500">OS</th>
                            <th className="px-4 py-2 text-xs font-medium text-gray-500">Statut</th>
                            <th className="px-4 py-2 text-xs font-medium text-gray-500">Attribué le</th>
                            {isAgent && <th className="px-4 py-2 text-xs font-medium text-gray-500 text-right">Actions</th>}
                        </tr></thead>
                        <tbody>
                            {devices.map((d) => {
                                const dtCfg = DEVICE_TYPE_CONFIG[d.device_type] || DEVICE_TYPE_CONFIG.other;
                                return (
                                    <tr key={d.id} className="border-b border-gray-50 hover:bg-gray-50" data-testid={`device-row-${d.id}`}>
                                        <td className="px-4 py-2.5 font-medium text-gray-900">{d.display_name || d.pc_name || "—"}</td>
                                        <td className="px-4 py-2.5"><span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${dtCfg.color}`}>{dtCfg.label}</span></td>
                                        <td className="px-4 py-2.5 text-xs text-gray-500">{d.manufacturer ? `${d.manufacturer} ${d.model}` : d.model || "—"}</td>
                                        <td className="px-4 py-2.5 text-xs text-gray-500 font-mono">{d.serial_number || "—"}</td>
                                        <td className="px-4 py-2.5 text-xs text-gray-500">{d.operating_system ? `${d.operating_system} ${d.os_version || ""}`.trim() : "—"}</td>
                                        <td className="px-4 py-2.5"><span className={`text-[9px] px-1.5 py-0.5 rounded-full font-medium ${d.status === "active" ? "bg-emerald-100 text-emerald-700" : "bg-gray-200 text-gray-600"}`}>{d.status === "active" ? "Actif" : "Restitué"}</span></td>
                                        <td className="px-4 py-2.5 text-xs text-gray-500">{d.assigned_at ? new Date(d.assigned_at).toLocaleDateString("fr-CH") : "—"}</td>
                                        {isAgent && (
                                            <td className="px-4 py-2.5 text-right">
                                                {d.status === "active" && <Button variant="ghost" size="sm" onClick={() => onReturn(d)} title="Restituer"><Undo2 className="w-3.5 h-3.5 text-gray-500" /></Button>}
                                            </td>
                                        )}
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
}

function LicensesTab({ licenses, isAgent, onAdd, onRevoke }) {
    return (
        <div className="card">
            <div className="card-header flex items-center justify-between">
                <div className="flex items-center gap-2">
                    <Key className="w-4 h-4 text-gray-500" />
                    <h2 className="text-sm font-semibold text-gray-800">Licences</h2>
                    <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-500 font-medium">{licenses.length}</span>
                </div>
                {isAgent && <Button variant="outline" size="sm" onClick={onAdd} data-testid="add-license-btn"><Plus className="w-3 h-3 mr-1" />Ajouter</Button>}
            </div>
            <div className="card-body p-0">
                {licenses.length === 0 ? <p className="text-xs text-gray-400 py-6 text-center">Aucune licence</p> : (
                    <table className="w-full text-sm">
                        <thead><tr className="border-b border-gray-100 text-left">
                            <th className="px-4 py-2 text-xs font-medium text-gray-500">Licence</th>
                            <th className="px-4 py-2 text-xs font-medium text-gray-500">Type</th>
                            <th className="px-4 py-2 text-xs font-medium text-gray-500">Éditeur</th>
                            <th className="px-4 py-2 text-xs font-medium text-gray-500">Début</th>
                            <th className="px-4 py-2 text-xs font-medium text-gray-500">Fin</th>
                            <th className="px-4 py-2 text-xs font-medium text-gray-500">Statut</th>
                            {isAgent && <th className="px-4 py-2 text-xs font-medium text-gray-500 text-right">Actions</th>}
                        </tr></thead>
                        <tbody>
                            {licenses.map((l) => {
                                const isExpired = l.end_date && new Date(l.end_date) < new Date() && l.status === "active";
                                const displayStatus = isExpired ? "expired" : l.status;
                                const sCfg = LICENSE_STATUS[displayStatus] || LICENSE_STATUS.active;
                                const licTypeLabels = { subscription: "Abonnement", perpetual: "Perpétuelle", trial: "Essai", free: "Gratuite" };
                                return (
                                    <tr key={l.id} className="border-b border-gray-50 hover:bg-gray-50" data-testid={`license-row-${l.id}`}>
                                        <td className="px-4 py-2.5 font-medium text-gray-900">{l.name}</td>
                                        <td className="px-4 py-2.5 text-xs text-gray-500">{licTypeLabels[l.license_type] || l.license_type}</td>
                                        <td className="px-4 py-2.5 text-xs text-gray-500">{l.vendor || "—"}</td>
                                        <td className="px-4 py-2.5 text-xs text-gray-500">{l.start_date ? new Date(l.start_date).toLocaleDateString("fr-CH") : "—"}</td>
                                        <td className="px-4 py-2.5 text-xs text-gray-500">{l.end_date ? new Date(l.end_date).toLocaleDateString("fr-CH") : "Perpétuelle"}</td>
                                        <td className="px-4 py-2.5"><span className={`text-[9px] px-1.5 py-0.5 rounded-full font-medium ${sCfg.color}`}>{sCfg.label}</span></td>
                                        {isAgent && (
                                            <td className="px-4 py-2.5 text-right">
                                                {l.status === "active" && <Button variant="ghost" size="sm" onClick={() => onRevoke(l)} title="Révoquer"><Trash2 className="w-3.5 h-3.5 text-red-400" /></Button>}
                                            </td>
                                        )}
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
}

function AssetsTab({ equipment, softLicenses, accesses, isAgent, navigate, onAdd, onReturn, onTransfer, onLost, onBroken }) {
    return (
        <div className="space-y-4">
            <Section title="Equipement actif" icon={Monitor} count={equipment.length} onAdd={isAgent ? () => onAdd() : null}>
                {equipment.length === 0 ? <EmptyRow text="Aucun équipement" /> : (
                    <table className="w-full text-sm">
                        <thead><tr className="border-b border-gray-100 text-left"><th className="px-4 py-2 text-xs font-medium text-gray-500">Nom</th><th className="px-4 py-2 text-xs font-medium text-gray-500">N° série</th><th className="px-4 py-2 text-xs font-medium text-gray-500">Attribué le</th><th className="px-4 py-2 text-xs font-medium text-gray-500">Ticket</th>{isAgent && <th className="px-4 py-2 text-xs font-medium text-gray-500 text-right">Actions</th>}</tr></thead>
                        <tbody>
                            {equipment.map((a) => (
                                <tr key={a.id} className="border-b border-gray-50 hover:bg-gray-50" data-testid={`asset-row-${a.id}`}>
                                    <td className="px-4 py-2.5 font-medium text-gray-900">{a.name}</td>
                                    <td className="px-4 py-2.5 text-xs text-gray-500 font-mono">{a.serial_number || "—"}</td>
                                    <td className="px-4 py-2.5 text-xs text-gray-500">{a.assigned_at ? new Date(a.assigned_at).toLocaleDateString("fr-CH") : "—"}</td>
                                    <td className="px-4 py-2.5">{a.source_ticket_number ? <button onClick={() => navigate(`/tickets/${a.source_ticket_id}`)} className="text-xs text-blue-600 hover:underline">{a.source_ticket_number}</button> : "—"}</td>
                                    {isAgent && <td className="px-4 py-2.5 text-right"><AssetActions asset={a} onReturn={onReturn} onTransfer={onTransfer} onLost={onLost} onBroken={onBroken} /></td>}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </Section>
            <Section title="Licences & Logiciels" icon={Key} count={softLicenses.length}>
                {softLicenses.length === 0 ? <EmptyRow text="Aucune licence" /> : (
                    <table className="w-full text-sm">
                        <thead><tr className="border-b border-gray-100 text-left"><th className="px-4 py-2 text-xs font-medium text-gray-500">Nom</th><th className="px-4 py-2 text-xs font-medium text-gray-500">Type</th><th className="px-4 py-2 text-xs font-medium text-gray-500">Attribué le</th>{isAgent && <th className="px-4 py-2 text-xs font-medium text-gray-500 text-right">Actions</th>}</tr></thead>
                        <tbody>
                            {softLicenses.map((a) => (
                                <tr key={a.id} className="border-b border-gray-50 hover:bg-gray-50"><td className="px-4 py-2.5 font-medium text-gray-900">{a.name}</td><td className="px-4 py-2.5"><span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${TYPE_CONFIG[a.type]?.color}`}>{TYPE_CONFIG[a.type]?.label}</span></td><td className="px-4 py-2.5 text-xs text-gray-500">{a.assigned_at ? new Date(a.assigned_at).toLocaleDateString("fr-CH") : "—"}</td>{isAgent && <td className="px-4 py-2.5 text-right"><AssetActions asset={a} onReturn={onReturn} onTransfer={onTransfer} onLost={onLost} onBroken={onBroken} /></td>}</tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </Section>
            <Section title="Accès" icon={ShieldCheck} count={accesses.length}>
                {accesses.length === 0 ? <EmptyRow text="Aucun accès" /> : (
                    <table className="w-full text-sm">
                        <thead><tr className="border-b border-gray-100 text-left"><th className="px-4 py-2 text-xs font-medium text-gray-500">Ressource</th><th className="px-4 py-2 text-xs font-medium text-gray-500">Attribué le</th>{isAgent && <th className="px-4 py-2 text-xs font-medium text-gray-500 text-right">Actions</th>}</tr></thead>
                        <tbody>
                            {accesses.map((a) => (
                                <tr key={a.id} className="border-b border-gray-50 hover:bg-gray-50"><td className="px-4 py-2.5 font-medium text-gray-900">{a.name}</td><td className="px-4 py-2.5 text-xs text-gray-500">{a.assigned_at ? new Date(a.assigned_at).toLocaleDateString("fr-CH") : "—"}</td>{isAgent && <td className="px-4 py-2.5 text-right"><AssetActions asset={a} onReturn={onReturn} onTransfer={onTransfer} onLost={onLost} onBroken={onBroken} /></td>}</tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </Section>
        </div>
    );
}

function InventoryTab({ items, navigate }) {
    if (items.length === 0) return <div className="card"><div className="card-body"><p className="text-xs text-gray-400 py-4 text-center">Aucun item d'inventaire assigné</p></div></div>;
    return (
        <div className="card">
            <div className="card-header flex items-center gap-2"><Package className="w-4 h-4 text-gray-500" /><h2 className="text-sm font-semibold text-gray-800">Inventaire assigné</h2><span className="text-[10px] px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-500 font-medium">{items.length}</span></div>
            <div className="card-body p-0">
                <table className="w-full text-sm">
                    <thead><tr className="border-b border-gray-100 text-left"><th className="px-4 py-2 text-xs font-medium text-gray-500">Nom</th><th className="px-4 py-2 text-xs font-medium text-gray-500">Catégorie</th><th className="px-4 py-2 text-xs font-medium text-gray-500">N° série</th><th className="px-4 py-2 text-xs font-medium text-gray-500">Asset Tag</th></tr></thead>
                    <tbody>
                        {items.map((item) => (
                            <tr key={item.id} className="border-b border-gray-50 hover:bg-gray-50 cursor-pointer" onClick={() => navigate(`/admin/inventory/${item.id}`)} data-testid={`inv-item-${item.id}`}>
                                <td className="px-4 py-2.5 font-medium text-gray-900">{item.name}</td>
                                <td className="px-4 py-2.5 text-xs text-gray-500">{item.category}</td>
                                <td className="px-4 py-2.5 text-xs text-gray-500 font-mono">{item.serial_number || "—"}</td>
                                <td className="px-4 py-2.5 text-xs text-gray-500 font-mono">{item.asset_tag || "—"}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

function TicketsTab({ tickets, navigate }) {
    const [filter, setFilter] = useState("all");
    const filtered = filter === "all" ? tickets : tickets.filter((t) => t.status === filter);
    return (
        <div className="card">
            <div className="card-header flex items-center justify-between">
                <div className="flex items-center gap-2"><Ticket className="w-4 h-4 text-gray-500" /><h2 className="text-sm font-semibold text-gray-800">Tickets liés</h2><span className="text-[10px] px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-500 font-medium">{filtered.length}</span></div>
                <select value={filter} onChange={(e) => setFilter(e.target.value)} className="text-xs border rounded-md px-2 py-1" data-testid="tickets-filter">
                    <option value="all">Tous</option><option value="new">Nouveau</option><option value="open">Ouvert</option><option value="in_progress">En cours</option><option value="resolved">Résolu</option><option value="closed">Fermé</option>
                </select>
            </div>
            <div className="card-body p-0">
                {filtered.length === 0 ? <p className="text-xs text-gray-400 py-6 text-center">Aucun ticket</p> : (
                    <div className="px-4 py-2 space-y-1 max-h-96 overflow-y-auto">
                        {filtered.map((t) => (
                            <div key={t.id} className="flex items-center gap-3 py-1.5 border-b border-gray-50 last:border-0 cursor-pointer hover:bg-gray-50 rounded" onClick={() => navigate(`/tickets/${t.id}`)} data-testid={`ticket-row-${t.id}`}>
                                <span className="text-xs text-gray-400 w-24 flex-shrink-0">{t.ticket_number}</span>
                                <span className="text-xs text-gray-800 flex-1 truncate">{t.title}</span>
                                <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-500">{t.status}</span>
                                <ChevronRight className="w-3 h-3 text-gray-300" />
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}

// ========== SHARED COMPONENTS ==========

function SummaryCard({ label, count, color, onClick }) {
    return (
        <button onClick={onClick} className={`px-3 py-3 rounded-lg bg-${color}-50 hover:bg-${color}-100 transition-colors text-center cursor-pointer`} data-testid={`summary-${label.toLowerCase()}`}>
            <p className={`text-xl font-bold text-${color}-700`}>{count}</p>
            <p className={`text-[10px] text-${color}-500`}>{label}</p>
        </button>
    );
}

function Section({ title, icon: Icon, count, children, onAdd }) {
    return (
        <div className="card">
            <div className="card-header flex items-center justify-between">
                <div className="flex items-center gap-2"><Icon className="w-4 h-4 text-gray-500" /><h2 className="text-sm font-semibold text-gray-800">{title}</h2>{count > 0 && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-500 font-medium">{count}</span>}</div>
                {onAdd && <Button variant="outline" size="sm" onClick={onAdd}><Plus className="w-3 h-3 mr-1" />Ajouter</Button>}
            </div>
            <div className="card-body p-0">{children}</div>
        </div>
    );
}

function EmptyRow({ text }) { return <p className="text-xs text-gray-400 py-6 text-center">{text}</p>; }

function AssetActions({ asset, onReturn, onTransfer, onLost, onBroken }) {
    return (
        <div className="flex items-center gap-0.5 justify-end">
            <Button variant="ghost" size="sm" onClick={() => onReturn(asset)} title="Restituer"><Undo2 className="w-3.5 h-3.5 text-gray-500" /></Button>
            <Button variant="ghost" size="sm" onClick={() => onTransfer(asset)} title="Transférer"><ArrowLeftRight className="w-3.5 h-3.5 text-gray-500" /></Button>
            <Button variant="ghost" size="sm" onClick={() => onLost(asset)} title="Perdu"><AlertTriangle className="w-3.5 h-3.5 text-orange-400" /></Button>
            <Button variant="ghost" size="sm" onClick={() => onBroken(asset)} title="Endommagé"><Trash2 className="w-3.5 h-3.5 text-red-400" /></Button>
        </div>
    );
}

// ========== DIALOGS ==========

function ReturnDialog({ returnDialog, setReturnDialog, returnCondition, setReturnCondition, returnNotes, setReturnNotes, onConfirm }) {
    return (
        <Dialog open={!!returnDialog} onOpenChange={(o) => { if (!o) setReturnDialog(null); }}>
            <DialogContent><DialogHeader><DialogTitle>Restitution de {returnDialog?.name}</DialogTitle></DialogHeader>
                <div className="space-y-3 py-2">
                    <div><label className="form-label text-xs">État du matériel</label><select value={returnCondition} onChange={(e) => setReturnCondition(e.target.value)} className="w-full px-3 py-2 text-sm border rounded-md" data-testid="return-condition"><option value="">Sélectionner...</option><option value="Bon état">Bon état</option><option value="Usure normale">Usure normale</option><option value="Endommagé">Endommagé</option><option value="Incomplet">Incomplet</option></select></div>
                    <div><label className="form-label text-xs">Notes</label><textarea value={returnNotes} onChange={(e) => setReturnNotes(e.target.value)} className="w-full px-3 py-2 text-sm border rounded-md resize-none" rows={2} data-testid="return-notes" /></div>
                </div>
                <DialogFooter><Button variant="outline" onClick={() => setReturnDialog(null)}>Annuler</Button><Button onClick={onConfirm} data-testid="confirm-return-btn">Confirmer</Button></DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function TransferDialog({ transferDialog, setTransferDialog, transferTarget, setTransferTarget, users, departments, userId, onConfirm }) {
    return (
        <Dialog open={!!transferDialog} onOpenChange={(o) => { if (!o) setTransferDialog(null); }}>
            <DialogContent><DialogHeader><DialogTitle>Transférer {transferDialog?.name}</DialogTitle></DialogHeader>
                <div className="py-2"><label className="form-label text-xs">Destinataire</label><UserSearchSelect users={users.filter((u) => u.is_active !== false && u.id !== userId)} departments={departments} value={transferTarget} onChange={setTransferTarget} placeholder="Sélectionner le destinataire..." /></div>
                <DialogFooter><Button variant="outline" onClick={() => setTransferDialog(null)}>Annuler</Button><Button onClick={onConfirm} disabled={!transferTarget} data-testid="confirm-transfer-btn">Transférer</Button></DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function AddAssetDialog({ open, onClose, form, setForm, onConfirm }) {
    return (
        <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
            <DialogContent><DialogHeader><DialogTitle>Ajouter un asset</DialogTitle></DialogHeader>
                <div className="space-y-3 py-2">
                    <div><label className="form-label text-xs">Type</label><select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className="w-full px-3 py-2 text-sm border rounded-md" data-testid="add-asset-type"><option value="equipment">Equipement</option><option value="license">Licence</option><option value="software">Logiciel</option><option value="access">Accès</option><option value="other">Autre</option></select></div>
                    <div><label className="form-label text-xs">Nom *</label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="add-asset-name" /></div>
                    <div><label className="form-label text-xs">N° série</label><Input value={form.serial_number} onChange={(e) => setForm({ ...form, serial_number: e.target.value })} data-testid="add-asset-serial" /></div>
                    <div><label className="form-label text-xs">Description</label><textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="w-full px-3 py-2 text-sm border rounded-md resize-none" rows={2} data-testid="add-asset-desc" /></div>
                </div>
                <DialogFooter><Button variant="outline" onClick={onClose}>Annuler</Button><Button onClick={onConfirm} data-testid="confirm-add-asset-btn">Créer</Button></DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function AddDeviceDialog({ open, onClose, form, setForm, onConfirm }) {
    return (
        <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
            <DialogContent className="max-w-lg">
                <DialogHeader><DialogTitle>Ajouter un appareil</DialogTitle></DialogHeader>
                <div className="space-y-3 py-2 max-h-[60vh] overflow-y-auto">
                    <div><label className="form-label text-xs">Type d'appareil *</label><select value={form.device_type} onChange={(e) => setForm({ ...form, device_type: e.target.value })} className="w-full px-3 py-2 text-sm border rounded-md" data-testid="add-device-type"><option value="pc">PC</option><option value="laptop">Laptop</option><option value="smartphone">Smartphone</option><option value="tablet">Tablette</option><option value="printer">Imprimante</option><option value="other">Autre</option></select></div>
                    <div><label className="form-label text-xs">Nom / PC-Name *</label><Input value={form.display_name} onChange={(e) => setForm({ ...form, display_name: e.target.value })} placeholder="PC-JDUPONT" data-testid="add-device-name" /></div>
                    <div className="grid grid-cols-2 gap-3">
                        <div><label className="form-label text-xs">Fabricant</label><Input value={form.manufacturer} onChange={(e) => setForm({ ...form, manufacturer: e.target.value })} placeholder="Dell" data-testid="add-device-manufacturer" /></div>
                        <div><label className="form-label text-xs">Modèle</label><Input value={form.model} onChange={(e) => setForm({ ...form, model: e.target.value })} placeholder="Latitude 5540" data-testid="add-device-model" /></div>
                    </div>
                    <div><label className="form-label text-xs">N° de série</label><Input value={form.serial_number} onChange={(e) => setForm({ ...form, serial_number: e.target.value })} placeholder="SN-ABC123" data-testid="add-device-serial" /></div>
                    <div className="grid grid-cols-2 gap-3">
                        <div><label className="form-label text-xs">Système d'exploitation</label><Input value={form.operating_system} onChange={(e) => setForm({ ...form, operating_system: e.target.value })} placeholder="Windows 11 Enterprise" data-testid="add-device-os" /></div>
                        <div><label className="form-label text-xs">Version OS</label><Input value={form.os_version} onChange={(e) => setForm({ ...form, os_version: e.target.value })} placeholder="23H2" data-testid="add-device-osversion" /></div>
                    </div>
                    <div><label className="form-label text-xs">Trust Type</label><Input value={form.trust_type} onChange={(e) => setForm({ ...form, trust_type: e.target.value })} placeholder="AzureAd" data-testid="add-device-trust" /></div>
                    <div className="flex items-center gap-2"><input type="checkbox" checked={form.is_managed} onChange={(e) => setForm({ ...form, is_managed: e.target.checked })} className="rounded border-gray-300" data-testid="add-device-mdm" /><label className="text-xs text-gray-600">Géré (MDM)</label></div>
                    <div><label className="form-label text-xs">Notes</label><textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="w-full px-3 py-2 text-sm border rounded-md resize-none" rows={2} data-testid="add-device-notes" /></div>
                </div>
                <DialogFooter><Button variant="outline" onClick={onClose}>Annuler</Button><Button onClick={onConfirm} data-testid="confirm-add-device-btn">Ajouter</Button></DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function AddLicenseDialog({ open, onClose, form, setForm, onConfirm }) {
    const [showKey, setShowKey] = useState(false);
    return (
        <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
            <DialogContent className="max-w-lg">
                <DialogHeader><DialogTitle>Ajouter une licence</DialogTitle></DialogHeader>
                <div className="space-y-3 py-2 max-h-[60vh] overflow-y-auto">
                    <div><label className="form-label text-xs">Nom de la licence *</label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Microsoft 365 E3" data-testid="add-license-name" /></div>
                    <div><label className="form-label text-xs">Type</label><select value={form.license_type} onChange={(e) => setForm({ ...form, license_type: e.target.value })} className="w-full px-3 py-2 text-sm border rounded-md" data-testid="add-license-type"><option value="subscription">Abonnement</option><option value="perpetual">Perpétuelle</option><option value="trial">Essai</option><option value="free">Gratuite</option></select></div>
                    <div><label className="form-label text-xs">Éditeur / Fournisseur</label><Input value={form.vendor} onChange={(e) => setForm({ ...form, vendor: e.target.value })} placeholder="Microsoft" data-testid="add-license-vendor" /></div>
                    <div className="relative"><label className="form-label text-xs">Clé de licence</label><div className="relative"><Input type={showKey ? "text" : "password"} value={form.license_key} onChange={(e) => setForm({ ...form, license_key: e.target.value })} placeholder="XXXXX-XXXXX-XXXXX" data-testid="add-license-key" /><button type="button" onClick={() => setShowKey(!showKey)} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">{showKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}</button></div></div>
                    <div className="grid grid-cols-2 gap-3">
                        <div><label className="form-label text-xs">Date de début</label><Input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} data-testid="add-license-start" /></div>
                        <div><label className="form-label text-xs">Date de fin</label><Input type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} data-testid="add-license-end" /></div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div><label className="form-label text-xs">Coût</label><Input type="number" step="0.01" value={form.cost} onChange={(e) => setForm({ ...form, cost: e.target.value })} placeholder="0.00" data-testid="add-license-cost" /></div>
                        <div><label className="form-label text-xs">Devise</label><select value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value })} className="w-full px-3 py-2 text-sm border rounded-md" data-testid="add-license-currency"><option value="CHF">CHF</option><option value="EUR">EUR</option><option value="USD">USD</option></select></div>
                    </div>
                    <div><label className="form-label text-xs">Notes</label><textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="w-full px-3 py-2 text-sm border rounded-md resize-none" rows={2} data-testid="add-license-notes" /></div>
                </div>
                <DialogFooter><Button variant="outline" onClick={onClose}>Annuler</Button><Button onClick={onConfirm} data-testid="confirm-add-license-btn">Ajouter</Button></DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
